import React, { useEffect, useContext, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Spin, Typography, Modal, Form, Input, Button, Radio, Divider, Table, Select, Space, Tag, Tooltip, Dropdown, message, Tabs } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { AuthContext, NavBarContext, AuthInfoContext } from '../App';
import { LinkOutlined, SearchOutlined, DownOutlined, BranchesOutlined, PlayCircleOutlined, PauseCircleOutlined, SendOutlined, CheckCircleOutlined, DownloadOutlined, ExclamationCircleOutlined, FileAddOutlined, QuestionCircleOutlined, SyncOutlined, ClockCircleOutlined, CloseCircleOutlined, FileTextOutlined, FormOutlined, FileExcelOutlined } from '@ant-design/icons';
const DEV_ENDPOINT = 'http://localhost:5051';
const PROD_ENDPOINT = 'https://api.steadyshuttle.com';
const BACKEND_ENDPOINT = process.env.NODE_ENV === 'development' ? DEV_ENDPOINT : PROD_ENDPOINT;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

function lightenColor(hexCode, amount) {
  // Remove the '#' symbol and convert hex code to RGB values
  hexCode = hexCode.replace(/^#/, '');
  const r = parseInt(hexCode.substr(0, 2), 16);
  const g = parseInt(hexCode.substr(2, 2), 16);
  const b = parseInt(hexCode.substr(4, 2), 16);

  // Adjust the RGB values to make the color lighter
  const newR = Math.min(255, r + amount);
  const newG = Math.min(255, g + amount);
  const newB = Math.min(255, b + amount);

  // Convert the adjusted RGB values back to a hex code
  const newHexCode = `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  return newHexCode;
}


function Admin() {
  const { verifyAuth } = useContext(AuthContext);
  const { toggleNavBarVisibility, setSelectedKey } = useContext(NavBarContext);
  const { authInfo } = useContext(AuthInfoContext);

  const [bookings, setBookings] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedDirection, setSelectedDirection] = useState(0);
  const [passengers, setPassengers] = useState([]);
  const [formSubmissionLoading, setFormSubmissionLoading] = useState(false);
  const [cidTagColours, setCidTagColours] = useState({});
  const [otherSelected, setOtherSelected] = useState(false);
  const [cost, setCost] = useState(0);
  const [search, setSearch] = useState('');
  const [showModifyDateModal, setShowModifyDateModal] = useState(false);
  const [modifyDateBooking, setModifyDateBooking] = useState('');
  const [modifyDateValue, setModifyDateValue] = useState('');
  const [lastUsedDate, setLastUsedDate] = useState('');
  const [hasFetchedAll, setHasFetchedAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('shuttle'); // 'shuttle' | 'kl' | 'all'

  // Bulk operations state
  const [selectedBookingIds, setSelectedBookingIds] = useState([]);
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);
  const [bulkOperationProgress, setBulkOperationProgress] = useState(null);
  const [showBulkDateModal, setShowBulkDateModal] = useState(false);
  const [bulkDateValue, setBulkDateValue] = useState('');

  const [totalMYR, setTotalMYR] = useState(0);
  const [totalSGD, setTotalSGD] = useState(0);

  // Cash for refunds state
  const [totalRefundSGD, setTotalRefundSGD] = useState(0);
  const [refundCutoffDate, setRefundCutoffDate] = useState(null);

  // Helper function to preserve selections across data updates
  const preserveSelections = (currentSelections, newData) => {
    const availableIds = newData.map(item => item._id);
    return currentSelections.filter(id => availableIds.includes(id));
  };

  // Clear all selections
  const clearAllSelections = () => {
    setSelectedBookingIds([]);
  };

  // Fetch refund projection from backend
  const fetchRefundProjection = async () => {
    try {
      const response = await axios.get(BACKEND_ENDPOINT + '/api/admin/refund-projection', {
        headers: {
          cid: authInfo.cid,
          sess: authInfo.session_key,
        },
      });
      if (response.data.success) {
        setTotalRefundSGD(response.data.total_refund_sgd);
        setRefundCutoffDate(response.data.cutoff_date);
      }
    } catch (error) {
      console.error('Error fetching refund projection:', error);
    }
  };

  const formRef = useRef(null);

  const fetchPassengers = () => {
    axios.get(BACKEND_ENDPOINT + '/api/all_passengers', {
      headers: {
        cid: authInfo.cid,
        sess: authInfo.session_key,
      },
    })
      .then((response) => {
        setPassengers(response.data.passengers);
      })
      .catch((error) => {
        console.error('Error fetching passengers:', error);
      });
  };

  const processBookingsData = (bookingsArray, bookingType) => {
    // Helper to generate consistent colors for CIDs
    const generateColor = (text) => {
      let color = cidTagColours[text];
      if (!color) {
        color = "#" + Math.floor(Math.random()*16777215).toString(16);
        lightenColor(color, 200);
      }
      return color;
    };

    // Normalize KL booking fields to match shuttle structure for table compatibility
    const data = bookingsArray.map((booking) => {
      const isKl = bookingType === 'kl' || booking.bookingType === 'kl';
      return {
        ...booking,
        key: booking._id,
        bookingType: isKl ? 'kl' : 'shuttle',
        // Normalize requestTime for KL bookings (uses createdAt instead)
        requestTime: isKl && booking.createdAt
          ? Math.floor(new Date(booking.createdAt).getTime() / 1000)
          : booking.requestTime,
        // Normalize targetDate for KL bookings
        targetDate: booking.targetDate || booking.targetDateFull,
      };
    });

    // Generate colors for CIDs
    const uniqueCids = [...new Set(data.map((b) => b.cid))];
    let newTagColors = [];
    uniqueCids.forEach((cid) => {
      let color = generateColor(cid);
      newTagColors.push({cid: cid, color: color});
    });
    let tempTagColours = { ...cidTagColours };
    newTagColors.forEach((tag) => {
      tempTagColours[tag.cid] = tag.color;
    });
    setCidTagColours(tempTagColours);

    return data;
  };

  const calculateTotals = (data) => {
    let myrCount = 0;
    for (let i = 0; i < data.length; i++) {
      const booking = data[i];
      const direction = parseInt(booking.targetDirection);
      if (booking.bookingType === 'kl') {
        // KL pricing: simplified flat rate for now
        myrCount += direction === 0 ? 50 : 50; // Adjust based on actual KL pricing
      } else {
        // Shuttle pricing
        if (direction === 0) {
          myrCount += 16.95;
        } else {
          myrCount += 5;
        }
      }
    }
    setTotalMYR(myrCount);
    setTotalSGD(myrCount / 3.2);
  };

  const fetchBookings = () => {
    setIsLoading(true);

    const headers = {
      cid: authInfo.cid,
      sess: authInfo.session_key,
      status: 'working'
    };

    if (activeTab === 'shuttle') {
      // Fetch only shuttle bookings
      axios.get(BACKEND_ENDPOINT + '/api/all_bookings', { headers })
        .then((response) => {
          const data = processBookingsData(response.data.bookings, 'shuttle');
          setBookings(data);
          setTableData(data);
          setSelectedBookingIds(prev => preserveSelections(prev, data));
          calculateTotals(data);
        })
        .catch((error) => {
          console.error('Error fetching shuttle bookings:', error);
          message.error('Failed to fetch shuttle bookings');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (activeTab === 'kl') {
      // Fetch only KL bookings
      axios.get(BACKEND_ENDPOINT + '/api/all_kl_bookings', { headers })
        .then((response) => {
          const data = processBookingsData(response.data.bookings, 'kl');
          setBookings(data);
          setTableData(data);
          setSelectedBookingIds(prev => preserveSelections(prev, data));
          calculateTotals(data);
        })
        .catch((error) => {
          console.error('Error fetching KL bookings:', error);
          message.error('Failed to fetch KL bookings');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // Fetch both and merge for 'all' tab
      Promise.all([
        axios.get(BACKEND_ENDPOINT + '/api/all_bookings', { headers }),
        axios.get(BACKEND_ENDPOINT + '/api/all_kl_bookings', { headers })
      ])
        .then(([shuttleRes, klRes]) => {
          const shuttleData = processBookingsData(shuttleRes.data.bookings, 'shuttle');
          const klData = processBookingsData(klRes.data.bookings, 'kl');
          // Merge and sort by requestTime (newest first)
          const merged = [...shuttleData, ...klData].sort((a, b) => b.requestTime - a.requestTime);
          setBookings(merged);
          setTableData(merged);
          setSelectedBookingIds(prev => preserveSelections(prev, merged));
          calculateTotals(merged);
        })
        .catch((error) => {
          console.error('Error fetching all bookings:', error);
          message.error('Failed to fetch bookings');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleFetchAll = () => {
    setHasFetchedAll(true);
    setIsLoading(true);

    const headers = {
      cid: authInfo.cid,
      sess: authInfo.session_key,
    };

    const fetchAndProcess = (responseData, bookingType) => {
      const data = processBookingsData(responseData.bookings, bookingType);
      setBookings(data);
      setTableData(data);
      setSelectedBookingIds(prev => preserveSelections(prev, data));
      calculateTotals(data);
      message.success(`Fetched ${data.length} ${bookingType} bookings (last 30 days + future)`);
      setSearch('');
    };

    if (activeTab === 'shuttle') {
      axios.get(BACKEND_ENDPOINT + '/api/all_bookings', { headers })
        .then((response) => fetchAndProcess(response.data, 'shuttle'))
        .catch((error) => {
          console.error('Error fetching shuttle bookings:', error);
          message.error('Failed to fetch shuttle bookings');
        })
        .finally(() => setIsLoading(false));
    } else if (activeTab === 'kl') {
      axios.get(BACKEND_ENDPOINT + '/api/all_kl_bookings', { headers })
        .then((response) => fetchAndProcess(response.data, 'kl'))
        .catch((error) => {
          console.error('Error fetching KL bookings:', error);
          message.error('Failed to fetch KL bookings');
        })
        .finally(() => setIsLoading(false));
    } else {
      // Fetch both for 'all' tab
      Promise.all([
        axios.get(BACKEND_ENDPOINT + '/api/all_bookings', { headers }),
        axios.get(BACKEND_ENDPOINT + '/api/all_kl_bookings', { headers })
      ])
        .then(([shuttleRes, klRes]) => {
          const shuttleData = processBookingsData(shuttleRes.data.bookings, 'shuttle');
          const klData = processBookingsData(klRes.data.bookings, 'kl');
          const merged = [...shuttleData, ...klData].sort((a, b) => b.requestTime - a.requestTime);
          setBookings(merged);
          setTableData(merged);
          setSelectedBookingIds(prev => preserveSelections(prev, merged));
          calculateTotals(merged);
          message.success(`Fetched ${merged.length} total bookings (${shuttleData.length} shuttle + ${klData.length} KL)`);
          setSearch('');
        })
        .catch((error) => {
          console.error('Error fetching all bookings:', error);
          message.error('Failed to fetch bookings');
        })
        .finally(() => setIsLoading(false));
    }
  };

  const handleAddBooking = async (bookingDetails) => {
    setFormSubmissionLoading(true);
    try {
      const formattedValues = {
        date: bookingDetails.date,
        direction: bookingDetails.direction,
        passenger: bookingDetails.passenger,
        train: bookingDetails.train,
      };
      await axios.post(BACKEND_ENDPOINT + '/api/bookings', formattedValues, {
        headers: {
          cid: authInfo.cid,
          sess: authInfo.session_key,
        },
      });
      formRef.current.resetFields();
      fetchBookings();
      setIsModalVisible(false);
      setFormSubmissionLoading(false);
    } catch (error) {
      console.error('Error adding booking:', error);
      console.log(error.response.data.error);
      setFormSubmissionLoading(false);
    }
  };


  const processSearch = () => {
    const searchText = search.trim();

    if (!searchText) {
      message.warning('Please enter a CID to search');
      return;
    }

    setIsLoading(true);

    const headers = {
      cid: authInfo.cid,
      sess: authInfo.session_key,
    };
    const params = { search_cid: searchText };

    if (activeTab === 'shuttle') {
      axios.get(BACKEND_ENDPOINT + '/api/all_bookings', { headers, params })
        .then((response) => {
          const data = processBookingsData(response.data.bookings, 'shuttle');
          setBookings(data);
          setTableData(data);
          setSelectedBookingIds(prev => preserveSelections(prev, data));
          calculateTotals(data);
          message.success(`Found ${data.length} shuttle bookings for CID: ${searchText}`);
        })
        .catch((error) => {
          console.error('Error searching shuttle bookings:', error);
          message.error('Failed to search shuttle bookings');
        })
        .finally(() => setIsLoading(false));
    } else if (activeTab === 'kl') {
      axios.get(BACKEND_ENDPOINT + '/api/all_kl_bookings', { headers, params })
        .then((response) => {
          const data = processBookingsData(response.data.bookings, 'kl');
          setBookings(data);
          setTableData(data);
          setSelectedBookingIds(prev => preserveSelections(prev, data));
          calculateTotals(data);
          message.success(`Found ${data.length} KL bookings for CID: ${searchText}`);
        })
        .catch((error) => {
          console.error('Error searching KL bookings:', error);
          message.error('Failed to search KL bookings');
        })
        .finally(() => setIsLoading(false));
    } else {
      // Search both for 'all' tab
      Promise.all([
        axios.get(BACKEND_ENDPOINT + '/api/all_bookings', { headers, params }),
        axios.get(BACKEND_ENDPOINT + '/api/all_kl_bookings', { headers, params })
      ])
        .then(([shuttleRes, klRes]) => {
          const shuttleData = processBookingsData(shuttleRes.data.bookings, 'shuttle');
          const klData = processBookingsData(klRes.data.bookings, 'kl');
          const merged = [...shuttleData, ...klData].sort((a, b) => b.requestTime - a.requestTime);
          setBookings(merged);
          setTableData(merged);
          setSelectedBookingIds(prev => preserveSelections(prev, merged));
          calculateTotals(merged);
          message.success(`Found ${merged.length} bookings for CID: ${searchText} (${shuttleData.length} shuttle + ${klData.length} KL)`);
        })
        .catch((error) => {
          console.error('Error searching all bookings:', error);
          message.error('Failed to search bookings');
        })
        .finally(() => setIsLoading(false));
    }
  }

  useEffect(() => {
    verifyAuth();
    setSelectedKey('6');
    toggleNavBarVisibility(true);
    fetchBookings();
    fetchRefundProjection();
    return () => {
      toggleNavBarVisibility(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when active tab changes
  useEffect(() => {
    if (authInfo?.cid) {
      clearAllSelections();
      fetchBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const columns = [
    {
      title: 'State',
      dataIndex: 'status',
      key: 'status',
      // defaultSortOrder: 'ascend',
      sorter: (a, b) => {
        switch (a.status) {
          case 'working':
            return -1;
          case 'reviewing':
            return b.status === 'working' ? 1 : -1;
          case 'payment_pending':
            return b.status === 'working' || b.status === 'reviewing' ? 1 : -1;
          case 'booked':
            return b.status === 'user_cancelled' || b.status === 'admin_cancelled' || b.status === 'refunded' || b.status === 'cancelled' || b.status === 'rejected' ? -1 : 1;
          case 'user_cancelled':
            return b.status === 'admin_cancelled' || b.status === 'refunded' || b.status === 'cancelled' || b.status === 'rejected' ? -1 : 1;
          case 'admin_cancelled':
            return b.status === 'refunded' || b.status === 'cancelled' || b.status === 'rejected' ? -1 : 1;
          case 'refunded':
            return b.status === 'cancelled' || b.status === 'rejected' ? -1 : 1;
          case 'cancelled':
            return b.status === 'rejected' ? -1 : 1;
          case 'rejected':
            return 1;
          default:
            return 0;
        }
      },
      filters: [
        {
          text: 'Working',
          value: 'working',
        },
        {
          text: 'Booked',
          value: 'booked',
        },
        {
          text: 'Cancelled',
          // all cancelled statuses
          value: ['user_cancelled', 'admin_cancelled', 'refunded', 'cancelled', 'rejected'],
        },
        {
          text: 'Reviewing',
          value: 'reviewing',
        }
      ],
      render: (text, record) => {
        if (text === "working") {
          return <Tooltip title={("Booking ID: " + record._id)}>
            <Tag color="processing" icon={<SyncOutlined spin />}/>
          </Tooltip>;
        } else if (text === "reviewing") {
          return <Tooltip title={("Booking ID: " + record._id)}>
            <Tag icon={<ClockCircleOutlined />} color="default">Pending Review</Tag>
          </Tooltip>;
        } else if (text === "paused") {
          return <Tag icon={<PauseCircleOutlined />} color="warning">Paused</Tag>;
        } else if (text === "payment_pending") {
          return <Tag icon={<ClockCircleOutlined />} color="gold">Pending Payment</Tag>;
        } else if (text === "queued") {
          return <Tooltip title={"We've put this booking into the queue. Queueing bookings links them together and helps prevent your passengers from being booked onto different train timeslots."}><Tag icon={<SyncOutlined />} color="gold"></Tag></Tooltip>;
        } else if (text === "booked") {
          return <Tag icon={<CheckCircleOutlined />} color="success">Booked</Tag>;
        } else if (text === "user_cancelled") {
          return <Tag icon={<CloseCircleOutlined />} color="error">Cancelled By You</Tag>;
        } else if (text === "admin_cancelled") {
          return <Tag icon={<CloseCircleOutlined />} color="error">Cancelled By Admin</Tag>;
        } else if (text === "refunded") {
          return <Tag icon={<CloseCircleOutlined />} color="error">Refunded</Tag>;
        } else if (text === "cancelled") {
          return <Tag icon={<CloseCircleOutlined />} color="error">Cancelled</Tag>;
        } else if (text === "rejected") {
          return (<Link to="/support"><Tag icon={<ExclamationCircleOutlined />} color="error">Rejected - Contact Support</Tag>
          </Link>);
        }
      }
    },
    {
      title: 'Type',
      dataIndex: 'bookingType',
      key: 'bookingType',
      render: (text) => (
        text === 'kl'
          ? <Tag color="purple">KL</Tag>
          : <Tag color="cyan">Shuttle</Tag>
      ),
      filters: [
        { text: 'Shuttle', value: 'shuttle' },
        { text: 'KL', value: 'kl' },
      ],
      onFilter: (value, record) => record.bookingType === value,
    },
    {
      title: 'Date',
      dataIndex: 'targetDate',
      key: 'targetDate',
      sorter: (a, b) => moment(a.targetDateFull) - moment(b.targetDateFull),
      render: (text, record) => (
        <Space>
          <Text>{moment(record.targetDateFull).format('D MMM (ddd)')}</Text>
          {(record.isCustomDate || record.is_custom_date || record.dateType === 'custom') && (
            <Tag color="orange">Custom</Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Book Time',
      dataIndex: 'requestTime',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.requestTime - b.requestTime,
      key: 'requestTime',
      render: (text) => {
        // accept text as unix timestamp, render as moment obj
        return <Space>
          <Text>{moment.unix(text).format('D MMM HH:mm')}</Text>
        </Space>
      }
    },
    {
      title: 'Remain',
      render: (text, record) => {
          const nowTime = moment();

          // KL/ETS bookings don't have fixed train schedules like shuttle
          // Show time until target date (midnight) instead
          if (record.bookingType === 'kl') {
            const targetTime = moment(record.targetDateFull, "YYYY-MM-DD").startOf('day');
            let diff = targetTime.diff(nowTime, 'minutes');
            if (diff < -1440) { // More than 1 day ago
              return <Text type="secondary">Past</Text>;
            } else if (diff < 0) {
              return <Text type="warning">Today</Text>;
            } else {
              // return in days, hours or mins depending on diff
              if (diff >= 1440) {
                const days = Math.floor(diff / 1440);
                const hours = Math.floor((diff % 1440) / 60);
                return <Text>{days}d {hours}h</Text>;
              }
              else if (diff >= 60) {
                const hours = Math.floor(diff / 60);
                const minutes = diff % 60;
                return <Text>{hours}h {minutes}m</Text>;
              }
              else {
                return <Text>{diff}m</Text>;
              }
            }
          }

          // Shuttle bookings - use hardcoded train schedule
          const direction = Number(record.targetDirection); // backend sends number; ensure comparison works
          const trainList = direction === 0 ? trainsTowardsJohor : trainsTowardsSingapore;
          const firstTrain = Array.isArray(record.targetTrain) ? record.targetTrain[0] : null;
          const trainMatch = trainList.find(train => train.value === firstTrain);
          if (!firstTrain || !trainMatch) {
            return <Text type="secondary">N/A</Text>;
          }
          const trainTiming = trainMatch.label.split(" ")[0];
          const targetTime = moment(record.targetDateFull + " " + trainTiming, "YYYY-MM-DD HH:mmA");

          let diff = targetTime.diff(nowTime, 'minutes');
          if (diff < 0) {
            return <Text type="danger">Departed</Text>
          } else {
            // return in days, hours or mins depending on diff
            if (diff >= 1440) {
              const days = Math.floor(diff / 1440);
              const hours = Math.floor((diff % 1440) / 60);
              return <Text>{days}d {hours}h</Text>;
            }
            else if (diff >= 60) {
              const hours = Math.floor(diff / 60);
              const minutes = diff % 60;
              return <Text>{hours}h {minutes}m</Text>;
            }
            else {
              return <Text>{diff}m</Text>;
            }

          }
      }
    },
    {
      title: (<>
        <Tooltip title="Hover over the train number tags to view the actual timing.">
          Train
        </Tooltip>
      </>),
      dataIndex: 'targetTrain',
      key: 'targetTrain',
      render: (text, record) => {
        // Handle KL bookings which use 'train' field and can have 'any' value
        if (record.bookingType === 'kl') {
          const trains = record.train || ['Any'];
          if (trains.includes('any') || trains.length === 0) {
            return <Tag color="blue">Any Train</Tag>;
          }
          return (
            <Space>
              {trains.map((train, idx) => (
                <Tag key={record._id + '-' + train + '-' + idx} color="purple">{train}</Tag>
              ))}
            </Space>
          );
        }

        // Shuttle bookings
        if (!text || text.length === 0) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Space>
            {text.map((train) => {
              const trainInfo = [
                ...trainsTowardsJohor,
                ...trainsTowardsSingapore,
              ].find(t => t.value === train);
              return (
                <Tooltip key={record._id + train} title={trainInfo ? <span>{trainInfo.label}</span> : train}>
                  <p>{train}</p>
                </Tooltip>
              );
            })}
          </Space>
        );
      }
    },
    {
      title: 'Dir',
      dataIndex: 'targetDirection',
      key: 'targetDirection',
      sorter: (a, b) => a.targetDirection - b.targetDirection,
      render: (text, record) => {
        // KL bookings: 0 = JB→KL, 1 = KL→JB
        // Shuttle bookings: 0 = Towards JB, 1 = Towards SG
        if (record.bookingType === 'kl') {
          return text === 0
            ? <Tooltip title="JB Sentral → KL Sentral"><Tag color="purple">JB→KL</Tag></Tooltip>
            : <Tooltip title="KL Sentral → JB Sentral"><Tag color="magenta">KL→JB</Tag></Tooltip>;
        }
        // Shuttle
        return text === 1
          ? <Tooltip title="Towards Singapore"><Tag color="orange">→SG</Tag></Tooltip>
          : <Tooltip title="Towards Johor"><Tag color="green">→JB</Tag></Tooltip>;
      }
    },
    {
      title: 'Class',
      dataIndex: 'serviceClass',
      key: 'serviceClass',
      render: (text, record) => {
        if (record.bookingType !== 'kl') return <Text type="secondary">-</Text>;
        const classColors = {
          'Silver': 'default',
          'Gold': 'gold',
          'Platinum': 'geekblue',
          'Express': 'red',
          'Standard': 'cyan',
        };
        return <Tag color={classColors[text] || 'default'}>{text || 'Standard'}</Tag>;
      }
    },
    {
      title:  'Action',
      key: '_id',
      render: (text, record) => 
          {

            const handleMenuClick = (e) => {
              // split record id and action id
              let recordId = e.key.split("_")[0];
              let actionId = e.key.split("_")[1];

              // Determine if this is a KL or shuttle booking
              const isKlBooking = record.bookingType === 'kl';
              const endpoint = isKlBooking
                ? `/api/kl-bookings/action/${recordId}`
                : `/api/bookings/action/${recordId}`;

              if (actionId === "6") {
                setModifyDateBooking(recordId)
                setModifyDateValue(lastUsedDate)
                setShowModifyDateModal(true);
                return
              } else {
                message.info('Processing action..');
              }
              // post to appropriate endpoint with action_code = actionId as a json parameter

              axios.post(BACKEND_ENDPOINT + endpoint, {
                action_code: actionId,
              }, {
                headers: {
                  cid: authInfo.cid,
                  sess: authInfo.session_key,
                }
              })
              .then(function (response) {
                console.log(response);
                message.success('Done!');
                // Refresh bookings after action
                fetchBookings();
              }
              )
              .catch(function (error) {
                console.log(error);
                message.error('Error processing action: ' + (error.response?.data?.error || error.message));
              }
              );
            };
            
            const items = [
              {
                label: 'Pause',
                key: record._id + '_3',
                // record: record,
                icon: <PauseCircleOutlined />,
                // danger: true,
              },
              {
                label: 'Resume',
                key: record._id + '_4',
                // record: record,
                icon: <PlayCircleOutlined />,
                // danger: true,
              },
              {
                label: 'Chain Job',
                key: record._id + '_5',
                icon: <BranchesOutlined />
              },
              {
                label: 'Patch Date',
                key: record._id + '_6',
                icon: <FormOutlined />
              },
              {
                label: 'Notify Refund',
                key: record._id + '_2',
                // record: record,
                icon: <SendOutlined/>,
                danger: true,
              },
              {
                label: 'Cancel',
                key: record._id + '_1',
                // record: record,
                icon: <CloseCircleOutlined />,
                danger: true,
              },
              {
                label: 'Approve',
                key: record._id + '_100',
                // record: record,
                // icon: <CloseCircleOutlined />,
                // danger: true,
              },
              {
                label: 'Reject',
                key: record._id + '_200',
                // record: record,
                // icon: <CloseCircleOutlined />,
                // danger: true,
              },
            ];
            const menuProps = {
              items,
              onClick: handleMenuClick,
            };
            return (
          <Space size="middle">
            <Dropdown menu={menuProps}>
              <Button>
                <Space>
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </Space>
        )
            }
    },
    {
      title: 'CID',
      dataIndex: 'cid',
      key: '_id',
      render : (text, record) => {
        let color = cidTagColours[text];
        return <Tag color={color}>{text}</Tag>
      }
    },
    {
      title: 'Passenger',
      dataIndex: ['passenger', 'name'],
      key: ['passenger', 'name'],
      sorter: (a, b) => a.passenger.name.localeCompare(b.passenger.name),
      render: (text, record) => (
        <Space>
          <Tooltip title={(
            <div>
              <Text style={{ color: "#fff" }}>Name: {record.passenger.name}</Text>
              <br />
              <Text style={{ color: "#fff" }}>Passport: {record.passenger.passport}</Text>
              <br />
              <Text style={{ color: "#fff" }}>Contact: {record.passenger.contact}</Text>
            </div>
          )}>

            <Text>{record.passenger.name.slice(0, 7)}{record.passenger.name.length > 7 && "..."}</Text>
          </Tooltip>
        </Space>
      )
    },
    {
      title: <><Tooltip title="Runner Last Refreshed">RLR</Tooltip></>,
      dataIndex: 'runnerLastRefreshed',
      key: 'runnerLastRefreshed',
      render: (text, record) => {
        if (record.runnerLastRefreshed) {
          if (moment(record.runnerLastRefreshed).isBefore(moment().subtract(30, 'minutes'))) { // change this to be (more than 30 mins ago)
            return <Text style={{ fontSize: "0.7em", fontWeight: "bolder", color: "red" }}>{moment(record.runnerLastRefreshed).fromNow().replace('minute', 'min').replace('seconds', 'sec')}</Text>
          } else {
            return <Text style={{ fontSize: "0.7em"}}>{moment(record.runnerLastRefreshed).fromNow().replace('minute', 'min').replace('seconds', 'sec')}</Text>
          }
        } else {
          return <Text style={{ fontWeight: "bolder", color: "red" }}>N/A</Text>
        }
      }
    },
    {
      title: <><Tooltip title="Last Load Time">LLT</Tooltip></>,
      dataIndex: 'runnerLastLoadTime',
      key: 'runnerLastLoadTime',
      render: (text, record) => {
        if (record.runnerLastLoadTime && record.runnerLastLoadTime > 0) {
          return <Text style={{ fontSize: "0.7em"}}>{record.runnerLastLoadTime}ms</Text>
        } else {
          return <Text>N/A</Text>
        }
      }
    },
    {
      title: <><Tooltip title="Payment Intent">PI</Tooltip></>,
      dataIndex: 'payment_intent',
      key: 'payment_intent',
      render: (text, record) => {
        if (record.payment_intent) {
          return <Button type="primary" shape="circle" icon={<LinkOutlined />} onClick={() => {
            window.open("https://dashboard.stripe.com/payments/" + record.payment_intent, "_blank")
          }}></Button>
        } else {
          return <Text style={{ fontSize: "0.7em"}}>-</Text>
        }
      }
    },
    {
      title: <><Tooltip title="Self Cancellable">SC</Tooltip></>,
      dataIndex: 'self_cancellable',
      key: 'self_cancellable',
      render: (text, record) => {
        if (record.self_cancellable) {
          return <Text style={{ fontSize: "0.7em", color: 'green'}}>Y</Text>
        } else {
          return <Text style={{ fontSize: "0.7em", color: 'red'}}>N</Text>
        }
      }
    }
  ];


  const handlePatchDate = (e) => {
    // split record id and action id

    let recordId = modifyDateBooking;
    let actionId = "6";
    let targetDate = modifyDateValue;

    // Find the booking to determine if it's KL or shuttle
    const booking = tableData.find(b => b._id === recordId);
    const isKlBooking = booking?.bookingType === 'kl';
    const endpoint = isKlBooking
      ? `/api/kl-bookings/action/${recordId}`
      : `/api/bookings/action/${recordId}`;

    axios.post(BACKEND_ENDPOINT + endpoint, {
      action_code: actionId,
      target_date: targetDate,
    }, {
      headers: {
        cid: authInfo.cid,
        sess: authInfo.session_key,
      }
    })
    .then(function (response) {
      console.log(response);
      message.success('Done!');
      
      // Store the successfully used date for future use
      setLastUsedDate(targetDate);
      
      // Update local state instead of re-fetching
      // Parse the target date to update all date fields
      const targetDateObj = new Date(targetDate);
      const targetDateFull = targetDate; // Already in YYYY-MM-DD format
      const targetDayOfWeek = moment(targetDate).format('dddd'); // Full day name like "Friday"
      const targetDateFormatted = moment(targetDate).format('D MMM'); // Format like "12 May"
      
      // Update bookings array
      const updatedBookings = bookings.map(booking => {
        if (booking._id === recordId) {
          return {
            ...booking,
            targetDateFull: targetDateFull,
            targetDateObj: targetDateObj.toISOString(),
            targetDayOfWeek: targetDayOfWeek,
            targetDate: targetDateFormatted
          };
        }
        return booking;
      });
      
      // Update tableData array
      const updatedTableData = tableData.map(booking => {
        if (booking._id === recordId) {
          return {
            ...booking,
            targetDateFull: targetDateFull,
            targetDateObj: targetDateObj.toISOString(),
            targetDayOfWeek: targetDayOfWeek,
            targetDate: targetDateFormatted
          };
        }
        return booking;
      });
      
      // Set the updated state
      setBookings(updatedBookings);
      setTableData(updatedTableData);
      
      // Close the modal
      setShowModifyDateModal(false);
      setModifyDateBooking(null);
      setModifyDateValue('');
    }
    )
    .catch(function (error) {
      console.log(error);
      message.error('Error processing action: ' + error.response.data.error);
    }
    );
  };

  const handleBulkPatchDate = async () => {
    if (!bulkDateValue.trim()) {
      message.error('Please enter a date');
      return;
    }

    setBulkOperationInProgress(true);
    setBulkOperationProgress({
      current: 0,
      total: selectedBookingIds.length,
      operation: 'Patching dates',
      errors: []
    });

    const targetDate = bulkDateValue.trim();
    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (let i = 0; i < selectedBookingIds.length; i++) {
      const recordId = selectedBookingIds[i];
      
      setBulkOperationProgress(prev => ({
        ...prev,
        current: i + 1,
        currentBookingId: recordId
      }));

      try {
        // Find the booking to determine if it's KL or shuttle
        const booking = tableData.find(b => b._id === recordId);
        const isKlBooking = booking?.bookingType === 'kl';
        const endpoint = isKlBooking
          ? `/api/kl-bookings/action/${recordId}`
          : `/api/bookings/action/${recordId}`;

        await axios.post(BACKEND_ENDPOINT + endpoint, {
          action_code: "6",
          target_date: targetDate,
        }, {
          headers: {
            cid: authInfo.cid,
            sess: authInfo.session_key,
          }
        });

        // Update local state for successful patch
        const targetDateObj = new Date(targetDate);
        const targetDateFull = targetDate;
        const targetDayOfWeek = moment(targetDate).format('dddd');
        const targetDateFormatted = moment(targetDate).format('D MMM');
        
        // Update bookings array
        const updatedBookings = bookings.map(booking => {
          if (booking._id === recordId) {
            return {
              ...booking,
              targetDateFull: targetDateFull,
              targetDateObj: targetDateObj.toISOString(),
              targetDayOfWeek: targetDayOfWeek,
              targetDate: targetDateFormatted
            };
          }
          return booking;
        });
        
        // Update tableData array
        const updatedTableData = tableData.map(booking => {
          if (booking._id === recordId) {
            return {
              ...booking,
              targetDateFull: targetDateFull,
              targetDateObj: targetDateObj.toISOString(),
              targetDayOfWeek: targetDayOfWeek,
              targetDate: targetDateFormatted
            };
          }
          return booking;
        });
        
        setBookings(updatedBookings);
        setTableData(updatedTableData);
        successCount++;

      } catch (error) {
        console.error(`Error patching date for booking ${recordId}:`, error);
        errors.push({
          bookingId: recordId,
          error: error.response?.data?.error || error.message
        });
        errorCount++;
      }

      // Small delay between requests to avoid overwhelming the server
      if (i < selectedBookingIds.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Store the successfully used date for future use
    if (successCount > 0) {
      setLastUsedDate(targetDate);
    }

    setBulkOperationInProgress(false);
    setBulkOperationProgress(null);
    setShowBulkDateModal(false);

    // Show summary message
    if (errorCount === 0) {
      message.success(`Successfully patched dates for all ${successCount} bookings`);
    } else if (successCount === 0) {
      message.error(`Failed to patch dates for all ${errorCount} bookings`);
    } else {
      message.warning(`Patched ${successCount} bookings successfully, ${errorCount} failed`);
    }

    // Show detailed errors if any
    if (errors.length > 0) {
      console.error('Bulk patch date errors:', errors);
    }
  };

  const handleBulkApprove = async () => {
    setBulkOperationInProgress(true);
    setBulkOperationProgress({
      current: 0,
      total: selectedBookingIds.length,
      operation: 'Approving bookings',
      errors: []
    });

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (let i = 0; i < selectedBookingIds.length; i++) {
      const recordId = selectedBookingIds[i];
      
      setBulkOperationProgress(prev => ({
        ...prev,
        current: i + 1,
        currentBookingId: recordId
      }));

      try {
        // Find the booking to determine if it's KL or shuttle
        const booking = tableData.find(b => b._id === recordId);
        const isKlBooking = booking?.bookingType === 'kl';
        const endpoint = isKlBooking
          ? `/api/kl-bookings/action/${recordId}`
          : `/api/bookings/action/${recordId}`;

        await axios.post(BACKEND_ENDPOINT + endpoint, {
          action_code: "100",
        }, {
          headers: {
            cid: authInfo.cid,
            sess: authInfo.session_key,
          }
        });

        successCount++;

      } catch (error) {
        console.error(`Error approving booking ${recordId}:`, error);
        errors.push({
          bookingId: recordId,
          error: error.response?.data?.error || error.message
        });
        errorCount++;
      }

      // Small delay between requests to avoid overwhelming the server
      if (i < selectedBookingIds.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    setBulkOperationInProgress(false);
    setBulkOperationProgress(null);

    // Refresh the bookings data to reflect the changes
    fetchBookings();

    // Show summary message
    if (errorCount === 0) {
      message.success(`Successfully approved all ${successCount} bookings`);
    } else if (successCount === 0) {
      message.error(`Failed to approve all ${errorCount} bookings`);
    } else {
      message.warning(`Approved ${successCount} bookings successfully, ${errorCount} failed`);
    }

    // Show detailed errors if any
    if (errors.length > 0) {
      console.error('Bulk approve errors:', errors);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleDelete = (id) => {
    // filter passenger from passengers based on matching id
    setSelectedBooking(bookings.filter((booking) => booking._id === id)[0])
    setIsDeleteModalVisible(true)
  };

  const handleDeleteConfirm = () => {
    console.log(`Deleting passenger ${selectedBooking.name} (${selectedBooking.passport})`);

    axios.delete(BACKEND_ENDPOINT + '/api/bookings/' + selectedBooking._id, {
      headers: {
        cid: authInfo.cid,
        sess: authInfo.session_key,
      },
    })
      .then((response) => {
        // console.log(response)
        fetchBookings();
      })
      .catch((error) => {
        console.error('Error deleting booking:', error);
      });

    setIsDeleteModalVisible(false);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
  };

  // eslint-disable-next-line no-unused-vars
  const handlePayment = (id) => {
    // get booking object from bookings based on matching id
    let payment_booking = bookings.filter((booking) => booking._id === id)[0]
    // open payment_booking.payment_url in new tab
    window.open(payment_booking.payment_url, "_blank")
  };



  const getWeekendDates = () => {
    const today = moment();
    const nextFriday = moment().day(5 + (today.day() > 5 ? 7 : 0));
    const nextSaturday = nextFriday.clone().add(1, 'day');
    const nextSunday = nextFriday.clone().add(2, 'day');
    const nextNextFriday = nextFriday.clone().add(7, 'day');
    const nextNextSaturday = nextSaturday.clone().add(7, 'day');
    const nextNextSunday = nextSunday.clone().add(7, 'day');
    return [nextFriday, nextSaturday, nextSunday, nextNextFriday, nextNextSaturday, nextNextSunday];
  };

  const trainsTowardsJohor = [
    { label: "8.30AM (Train #72)", value: 72 },
    { label: "9.45AM (Train #74)", value: 74 },
    { label: "11.00AM (Train #76)", value: 76 },
    { label: "12.30PM (Train #78)", value: 78 },
    { label: "1.45PM (Train #80)", value: 80 },
    { label: "3.00PM (Train #82)", value: 82 },
    { label: "4.15PM (Train #84)", value: 84 },
    { label: "5.30PM (Train #86)", value: 86 },
    { label: "6.45PM (Train #88)", value: 88 },
    { label: "8.00PM (Train #90)", value: 90 },
    { label: "9.15PM (Train #92)", value: 92 },
    { label: "10.30PM (Train #94)", value: 94 },
    { label: "11.45PM (Train #96)", value: 96 }
  ];

  const trainsTowardsSingapore = [
    { label: "5.00AM (Train #61)", value: 61 },
    { label: "5.30AM (Train #63)", value: 63 },
    { label: "6.00AM (Train #65)", value: 65 },
    { label: "6.30AM (Train #67)", value: 67 },
    { label: "7.00AM (Train #69)", value: 69 },
    { label: "7.30AM (Train #71)", value: 71 },
    { label: "8.45AM (Train #73)", value: 73 },
    { label: "10.00AM (Train #75)", value: 75 },
    { label: "11.30AM (Train #77)", value: 77 },
    { label: "12.45PM (Train #79)", value: 79 },
    { label: "2.00PM (Train #81)", value: 81 },
    { label: "3.15PM (Train #83)", value: 83 },
    { label: "4.30PM (Train #85)", value: 85 },
    { label: "5.45PM (Train #87)", value: 87 },
    { label: "7.00PM (Train #89)", value: 89 },
    { label: "8.15PM (Train #91)", value: 91 },
    { label: "9.30PM (Train #93)", value: 93 },
    { label: "10.45PM (Train #95)", value: 95 }
  ];

  const showInitialSpinner = isLoading && tableData.length === 0;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', minHeight: '70vh' }}>
      <div style={{ textAlign: 'left', width: '100%' }}>
        <Title level={5} style={{ marginTop: '0em' }}><FileTextOutlined /> Booking Admin Dashboard</Title>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            clearAllSelections();
            setSearch('');
            setHasFetchedAll(false);
          }}
          style={{ marginBottom: 16 }}
          items={[
            { key: 'shuttle', label: 'Shuttle' },
            { key: 'kl', label: 'KL' },
            { key: 'all', label: 'All' },
          ]}
        />

        <Space style={{ marginBottom: 8, marginRight: 8 }}>
          {/* <Button type="primary" onClick={() => setIsModalVisible(true)}>
            <FileAddOutlined /> Add Booking
          </Button> */}
        <Button
          onClick={() => handleFetchAll()}
          loading={isLoading}
          disabled={isLoading}
          title="Fetch bookings from the last 30 days and all future bookings"
        >
          <DownloadOutlined />Fetch Recent
        </Button>
        </Space>
        <Space style={{ marginBottom: 8, marginRight: 8 }}>
        <p>
        Cash for bookings - <Tag color='cyan'>RM{totalMYR.toFixed(2)}</Tag><Tag color='blue'>S${totalSGD.toFixed(2)}</Tag>
        {refundCutoffDate && (
          <> | Cash for refunds ({refundCutoffDate}) - <Tag color='orange'>S${totalRefundSGD.toFixed(2)}</Tag></>
        )}
        </p>
        </Space>
        <Space style={{ marginBottom: 16 }}>
          <Input 
            placeholder="Search by CID" 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => processSearch()}
            disabled={isLoading}
          />
          <Button 
            onClick={() => processSearch()} 
            loading={isLoading}
            disabled={isLoading}
          >
            <SearchOutlined />
          </Button>
          {(search || hasFetchedAll) && (
            <Button 
              onClick={() => {
                setSearch('');
                setHasFetchedAll(false);
                fetchBookings();
              }}
              disabled={isLoading}
            >
              Clear
            </Button>
          )}
        </Space>
        
        {/* Bulk Operations Panel */}
        {selectedBookingIds.length > 0 && (
          <div style={{ 
            marginBottom: 16, 
            padding: 12, 
            backgroundColor: '#f0f8ff', 
            border: '1px solid #d9d9d9', 
            borderRadius: 6 
          }}>
            <Space wrap>
              <Text strong>
                {selectedBookingIds.length} booking{selectedBookingIds.length > 1 ? 's' : ''} selected
              </Text>
              <Button 
                type="primary" 
                icon={<FormOutlined />}
                onClick={() => {
                  setBulkDateValue(lastUsedDate);
                  setShowBulkDateModal(true);
                }}
                disabled={bulkOperationInProgress}
              >
                Bulk Patch Date
              </Button>
              <Button 
                type="primary" 
                icon={<CheckCircleOutlined />}
                onClick={() => handleBulkApprove()}
                disabled={bulkOperationInProgress}
              >
                Bulk Approve
              </Button>
              <Button 
                onClick={clearAllSelections}
                disabled={bulkOperationInProgress}
              >
                Clear Selection
              </Button>
            </Space>
          </div>
        )}
        
        {showInitialSpinner ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
            <Spin size="large" tip="Loading bookings..." />
          </div>
        ) : (
          <Spin spinning={isLoading} tip="Loading bookings...">
            <div style={{ overflowX: 'auto' }}>
              <Table 
                columns={columns} 
                dataSource={tableData} 
                rowKey="_id"
                scroll={{ x: 'max-content' }}
                rowSelection={{
                  selectedRowKeys: selectedBookingIds,
                  onChange: (selectedRowKeys) => {
                    setSelectedBookingIds(selectedRowKeys);
                  },
                  onSelectAll: (selected, selectedRows, changeRows) => {
                    if (selected) {
                      const allIds = tableData.map(booking => booking._id);
                      setSelectedBookingIds(allIds);
                    } else {
                      setSelectedBookingIds([]);
                    }
                  },
                  disabled: bulkOperationInProgress,
                }}
                pagination={{
                  defaultPageSize: 50,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100']
                }}
              />
            </div>
          </Spin>
        )}
        <Modal
          title={<><FileAddOutlined /> Add Booking</>}
          // visible={isModalVisible}
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
        >
          <Form
            layout="vertical"
            ref={formRef}
            onFinish={handleAddBooking}
          >

            <Form.Item
              name="direction"
              label="Direction"
              rules={[
                {
                  required: true,
                  message: 'Please choose a direction',
                },
              ]}
            >
              <Radio.Group
                onChange={(e) => {
                  // formRef.current.setFieldsValue({ train: [] });
                  setSelectedDirection(e.target.value);
                  setCost(e.target.value === 0 ? 9.4 : 8.8);
                }}
              >
              <Radio value={0}>Towards Johor ($9.40)</Radio>
              <Radio value={1}>Towards Singapore ($8.80)</Radio>
              </Radio.Group>
            </Form.Item>


            <Form.Item
              name="passenger"
              label="Passenger"
              rules={[
                {
                  required: true,
                  message: 'Please choose a passenger',
                },
              ]}
            >
              <Select
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                optionFilterProp="label"
                loading={!authInfo}
                onChange={(value) => {
                  formRef.current.setFieldsValue({ passenger: value });
                }}
                onFocus={fetchPassengers}
              >
                {passengers.map((passenger) => (
                  <Option key={passenger._id} value={passenger._id} label={passenger.name}>
                    {passenger.name} ({passenger.passport})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="date"
              label="Date"
              rules={[
                {
                  required: true,
                  message: 'Please choose a date',
                },
              ]}
            >
              <Select
                dropdownRender={(menu) => (
                  <div>
                    {menu}
                    {/* <Divider style={{ margin: '4px 0' }} />
                  <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8 }}>
                    <DatePicker
                      style={{ flex: 1 }}
                      format="ddd, DD MMM YYYY"
                      disabledDate={(current) => current && current < moment().endOf('day')}
                    />
                  </div> */}
                  </div>
                )}
                onChange={(value) => {
                  if (value === "0000-00-00") {
                    setOtherSelected(true)
                  } else {
                    setOtherSelected(false)
                  }
                }}
              >
                <Option key="other" value="0000-00-00">
                  All Other Dates (Mon-Thu) -{" "}
                  <Tooltip title="This option incurs an additional cost because it requires manual processing.">
                    +50¢ <QuestionCircleOutlined />
                  </Tooltip>
                </Option>
                {getWeekendDates().map((date) => (
                  <Option key={date.format('YYYY-MM-DD')} value={date.format('YYYY-MM-DD')}>
                    {date.format('ddd, DD MMM YYYY')}
                  </Option>
                ))}
              </Select>
            </Form.Item>


            <Form.Item
              name="train"
              label="Train"
              rules={[{ required: true, message: 'Please choose a train' }]}
              style={{
                marginBottom: "0"
              }}
            >
              <Select
                mode="multiple"
                placeholder="Please select train(s)"
              >
                {selectedDirection === 0
                  ? trainsTowardsJohor.map((train) => (
                    <Select.Option key={train.value} value={train.value}>
                      {train.label}
                    </Select.Option>
                  ))
                  : trainsTowardsSingapore.map((train) => (
                    <Select.Option key={train.value} value={train.value}>
                      {train.label}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
            <Text style={{
              fontSize: "0.8em",
              color: "#969696",
              marginTop: "-1em",
            }}>Choose several train slots to increase the likelihood of getting a ticket quickly. We'll book the first available train among those selected.</Text>
            <Divider />

            <Form.Item>
              <Text strong>Total Cost: S${(cost + (otherSelected ? 0.5 : 0)).toFixed(2)}</Text>
              {
                otherSelected &&
                <Paragraph>
                  <Text style={{
                    color: 'red',
                  }}>Please contact support after your booking request is made to let us know what date you'd like to book for. </Text>
                </Paragraph>
              }
              <Paragraph>
                <Text style={{
                  fontSize: "0.8em",
                  color: "#969696"
                }}>Next Steps: Your booking request will be reviewed by our team, and you'll be notified to make payment after it has been approved.</Text>
              </Paragraph>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" disabled={formSubmissionLoading} style={{ marginRight: 8 }}>
                {
                  formSubmissionLoading ?
                    <>
                      <SyncOutlined spin /> Loading
                    </>
                    :
                    <>
                      <CheckCircleOutlined /> Confirm Booking Details
                    </>
                }
              </Button>
              <Button onClick={() => setIsModalVisible(false)} style={{ marginRight: 8 }}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Modal>
        <Modal
          title={<><FileExcelOutlined /> Confirm Cancellation</>}
          // visible={isDeleteModalVisible}
          open={isDeleteModalVisible}
          onCancel={handleDeleteCancel}
          footer={[
            <Button key="delete" type="primary" danger onClick={handleDeleteConfirm}>
              Confirm Cancellation
            </Button>,
            <Button key="cancel" onClick={handleDeleteCancel}>
              Go Back
            </Button>,
          ]}
        >
          <Paragraph>
            Are you sure you want to cancel booking for {selectedBooking?.passenger.name} ({selectedBooking?.passenger.passport}) on {selectedBooking?.targetDate} (Train {selectedBooking?.targetTrain})?
          </Paragraph>

          <Paragraph>
            <ExclamationCircleOutlined /> This action cannot be reversed.
          </Paragraph>
        </Modal>

        <Modal
          title={<>Modify Date</>}
          open={showModifyDateModal}
          onCancel={ () => { setShowModifyDateModal(false)} }
          onOk={() => {
            setShowModifyDateModal(false)
            message.info("Patching date...")
            handlePatchDate()
            // alert(modifyDateValue)
          }}
          >
            <Paragraph>
              Modifying for {modifyDateBooking}
            </Paragraph>
            <Input
              placeholder="YYYY-MM-DD"
              value={modifyDateValue}
              onChange={(e) => {
                setModifyDateValue(e.target.value)
              }}
            >

            </Input>
        </Modal>

        {/* Bulk Date Modal */}
        <Modal
          title={<>Bulk Patch Date</>}
          open={showBulkDateModal}
          onCancel={() => setShowBulkDateModal(false)}
          onOk={() => {
            setShowBulkDateModal(false);
            message.info("Starting bulk date patch...");
            handleBulkPatchDate();
          }}
          disabled={bulkOperationInProgress}
        >
          <Paragraph>
            Patching date for {selectedBookingIds.length} booking{selectedBookingIds.length > 1 ? 's' : ''}
          </Paragraph>
          <Input
            placeholder="YYYY-MM-DD"
            value={bulkDateValue}
            onChange={(e) => setBulkDateValue(e.target.value)}
          />
        </Modal>

        {/* Progress Modal */}
        <Modal
          title={bulkOperationProgress?.operation || 'Processing...'}
          open={bulkOperationInProgress}
          closable={false}
          footer={null}
        >
          {bulkOperationProgress && (
            <div>
              <Paragraph>
                Processing: {bulkOperationProgress.current} of {bulkOperationProgress.total}
              </Paragraph>
              {bulkOperationProgress.currentBookingId && (
                <Paragraph>
                  Current booking: {bulkOperationProgress.currentBookingId}
                </Paragraph>
              )}
              <div style={{ width: '100%', backgroundColor: '#f0f0f0', borderRadius: 10, overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${(bulkOperationProgress.current / bulkOperationProgress.total) * 100}%`,
                    backgroundColor: '#1890ff',
                    height: 20,
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
              <Text style={{ fontSize: '0.9em', color: '#666', marginTop: 8 }}>
                {Math.round((bulkOperationProgress.current / bulkOperationProgress.total) * 100)}% complete
              </Text>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default Admin;
